<template>
  <div class="project-investment-deduction-allowance">
    <b-row v-if="project">
      <b-col lg="12" md="12" sm="12">
        <legend>
          <strong>{{ $t('iabCalculation') }}: {{ $t('generate_report') }}</strong>
        </legend>
        <b-row class="mb-3">
          <b-col>
            <ButtonWait
              v-if="project.number && project.number !== '_new'"
              :startCallback="startIabExportBuild"
              :pollCallback="pollIabExportBuildState"
              class="mr-2"
            >
              <template v-slot:buttonText>
                {{ $t('generate_report') }}
              </template>
            </ButtonWait>
            <div
              v-if="project.iabExportAttachments && project.iabExportAttachments.length > 0"
              class="export-files-section mt-2"
            >
              <AttachmentList
                :attachments="project.iabExportAttachments"
                attachmentType="iabExportAttachments"
                :inline="true"
                v-on:attachment-list:delete="onDeleteIabExportAttachment"
              />
            </div>
            <legend class="mt-3">
              <strong>{{ $t('finModelExport') }}: {{ $t('generate_report') }}</strong>
            </legend>
            <ButtonWait
              v-if="project.number && project.number !== '_new'"
              :startCallback="startFinModelBuild"
              :pollCallback="pollFinModelBuildState"
              class="mr-2"
            >
              <template v-slot:buttonText>
                {{ $t('generate_report') }}
              </template>
            </ButtonWait>
            <div v-if="finModelAttachments.length > 0" class="export-files-section mt-2">
              <AttachmentList
                :attachments="finModelAttachments"
                attachmentType="FIN_MODEL"
                :inline="true"
                v-on:attachment-list:delete="onDeleteFinModelAttachment"
              />
            </div>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
    <b-row v-if="iab">
      <b-col lg="6" md="6" sm="12">
        <legend>
          <strong>{{ $t('systemData') }}</strong>
        </legend>

        <b-form-group
          horizontal
          :label="$t('iabInstallationType')"
          label-for="installationType"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-input
            id="installationType"
            v-model="iab.installationType"
            type="text"
            :placeholder="$t('iabInstallationType')"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('investmentDeductionAllowance_areaInSquareMeters')"
          label-for="areaInSquareMeters"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <InputNumber
            id="areaInSquareMeters"
            v-model="iab.areaInSquareMeters"
            mode="decimal"
            :minFractionDigits="0"
            :maxFractionDigits="2"
            suffix=" m²"
            locale="de-DE"
            :placeholder="$t('investmentDeductionAllowance_areaInSquareMeters')"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('specificAnnualYieldKwhPerKwp')"
          label-for="specificAnnualYieldKwhPerKwp"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <InputNumber
            id="specificAnnualYieldKwhPerKwp"
            v-model="iab.specificAnnualYieldKwhPerKwp"
            mode="decimal"
            :minFractionDigits="2"
            :maxFractionDigits="2"
            locale="de-DE"
            :placeholder="$t('specificAnnualYieldKwhPerKwp')"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('initialFeedInTariffDurationYears')"
          label-for="initialFeedInTariffDurationYears"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <InputNumber
            id="initialFeedInTariffDurationYears"
            v-model="iab.initialFeedInTariffDurationYears"
            mode="decimal"
            :minFractionDigits="2"
            :maxFractionDigits="2"
            locale="de-DE"
            :placeholder="$t('initialFeedInTariffDurationYears')"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('calculatedEEGfeedInTariffEurPerKwh')"
          label-for="calculatedEEGfeedInTariffEurPerKwh"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <div class="form-control-plaintext">
            {{ calculatedEEGfeedInTariffEurPerKwhFormatted }}
          </div>
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('feedInTariffGuaranteeCaseEurPerKwh')"
          label-for="feedInTariffGuaranteeCaseEurPerKwh"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <InputNumber
            id="feedInTariffGuaranteeCaseEurPerKwh"
            v-model="iab.feedInTariffGuaranteeCaseEurPerKwh"
            mode="decimal"
            :minFractionDigits="5"
            :maxFractionDigits="5"
            locale="de-DE"
            :placeholder="$t('feedInTariffGuaranteeCaseEurPerKwh')"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('feedInTariffMedianCaseEurPerKwh')"
          label-for="feedInTariffMedianCaseEurPerKwh"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <InputNumber
            id="feedInTariffMedianCaseEurPerKwh"
            v-model="iab.feedInTariffMedianCaseEurPerKwh"
            mode="decimal"
            :minFractionDigits="5"
            :maxFractionDigits="5"
            locale="de-DE"
            :placeholder="$t('feedInTariffMedianCaseEurPerKwh')"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('feedInTariffOptimalCaseEurPerKwh')"
          label-for="feedInTariffOptimalCaseEurPerKwh"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <InputNumber
            id="feedInTariffOptimalCaseEurPerKwh"
            v-model="iab.feedInTariffOptimalCaseEurPerKwh"
            mode="decimal"
            :minFractionDigits="5"
            :maxFractionDigits="5"
            locale="de-DE"
            :placeholder="$t('feedInTariffOptimalCaseEurPerKwh')"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('expectedFeedInTariffIncreasePercentPerYear')"
          label-for="expectedFeedInTariffIncreasePercentPerYear"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <InputNumber
            id="expectedFeedInTariffIncreasePercentPerYear"
            v-model="iab.expectedFeedInTariffIncreasePercentPerYear"
            mode="decimal"
            :minFractionDigits="2"
            :maxFractionDigits="2"
            suffix=" %"
            locale="de-DE"
            :placeholder="$t('expectedFeedInTariffIncreasePercentPerYear')"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('degradationPercentPerYear')"
          label-for="degradationPercentPerYear"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <InputNumber
            id="degradationPercentPerYear"
            v-model="iab.degradationPercentPerYear"
            mode="decimal"
            :minFractionDigits="2"
            :maxFractionDigits="2"
            suffix=" %"
            locale="de-DE"
            :placeholder="$t('degradationPercentPerYear')"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('annualOperatingCostPerKwpEur')"
          label-for="annualOperatingCostPerKwpEur"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <InputNumber
            id="annualOperatingCostPerKwpEur"
            v-model="iab.annualOperatingCostPerKwpEur"
            mode="decimal"
            :minFractionDigits="0"
            :maxFractionDigits="0"
            locale="de-DE"
            :placeholder="$t('annualOperatingCostPerKwpEur')"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('annualLeaseType')"
          label-for="annualLeaseType"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-input
            id="annualLeaseType"
            v-model="iab.annualLeaseType"
            type="text"
            :placeholder="$t('annualLeaseType')"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('annualLeasePercent')"
          label-for="annualLeasePercent"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <InputNumber
            id="annualLeasePercent"
            v-model="iab.annualLeasePercent"
            mode="decimal"
            :minFractionDigits="2"
            :maxFractionDigits="2"
            suffix=" %"
            locale="de-DE"
            :placeholder="$t('annualLeasePercent')"
          />
        </b-form-group>
      </b-col>

      <b-col lg="6" md="6" sm="12">
        <legend>
          <strong>{{ $t('investmentData') }}</strong>
        </legend>

        <b-form-group
          horizontal
          :label="$t('totalInvestmentNetEur')"
          label-for="totalInvestmentNetEur"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <InputNumber
            id="totalInvestmentNetEur"
            v-model="iab.totalInvestmentNetEur"
            mode="currency"
            currency="EUR"
            currencyDisplay="code"
            :minFractionDigits="2"
            :maxFractionDigits="2"
            locale="de-DE"
            :placeholder="$t('totalInvestmentNetEur')"
          />
        </b-form-group>

        <legend>
          <strong>{{ $t('financingData') }}</strong>
        </legend>

        <b-form-group
          horizontal
          :label="$t('durationYears')"
          label-for="durationYears"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <InputNumber
            id="durationYears"
            v-model="iab.durationYears"
            mode="decimal"
            :minFractionDigits="2"
            :maxFractionDigits="2"
            locale="de-DE"
            :placeholder="$t('durationYears')"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('interestRatePercent')"
          label-for="interestRatePercent"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <InputNumber
            id="interestRatePercent"
            v-model="iab.interestRatePercent"
            mode="decimal"
            :minFractionDigits="2"
            :maxFractionDigits="2"
            suffix=" %"
            locale="de-DE"
            :placeholder="$t('interestRatePercent')"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('fixedRateYears')"
          label-for="fixedRateYears"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <InputNumber
            id="fixedRateYears"
            v-model="iab.fixedRateYears"
            mode="decimal"
            :minFractionDigits="2"
            :maxFractionDigits="2"
            locale="de-DE"
            :placeholder="$t('fixedRateYears')"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('repaymentRatePercent')"
          label-for="repaymentRatePercent"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <InputNumber
            id="repaymentRatePercent"
            v-model="iab.repaymentRatePercent"
            mode="decimal"
            :minFractionDigits="2"
            :maxFractionDigits="2"
            suffix=" %"
            locale="de-DE"
            :placeholder="$t('repaymentRatePercent')"
          />
        </b-form-group>

        <legend>
          <strong>{{ $t('taxData') }}</strong>
        </legend>

        <b-form-group
          horizontal
          :label="$t('taxTable')"
          label-for="taxTable"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-input
            id="taxTable"
            v-model="iab.taxTable"
            type="text"
            :placeholder="$t('taxTable')"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('effectiveTaxRatePercent')"
          label-for="effectiveTaxRatePercent"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <InputNumber
            id="effectiveTaxRatePercent"
            v-model="iab.effectiveTaxRatePercent"
            mode="decimal"
            :minFractionDigits="2"
            :maxFractionDigits="2"
            suffix=" %"
            locale="de-DE"
            :placeholder="$t('effectiveTaxRatePercent')"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('churchTaxRatePercent')"
          label-for="churchTaxRatePercent"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <Dropdown
            id="churchTaxRatePercent"
            v-model="iab.churchTaxRatePercent"
            :options="churchTaxRateOptions"
            optionLabel="label"
            optionValue="value"
            :placeholder="$t('churchTaxRatePercent')"
            :showClear="true"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('taxRatePercent')"
          label-for="taxRatePercent"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <InputNumber
            id="taxRatePercent"
            v-model="iab.taxRatePercent"
            mode="decimal"
            :minFractionDigits="2"
            :maxFractionDigits="2"
            suffix=" %"
            locale="de-DE"
            :placeholder="$t('taxRatePercent')"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('taxableIncomeEur')"
          label-for="taxableIncomeEur"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <InputNumber
            id="taxableIncomeEur"
            v-model="iab.taxableIncomeEur"
            mode="currency"
            currency="EUR"
            currencyDisplay="code"
            :minFractionDigits="2"
            :maxFractionDigits="2"
            locale="de-DE"
            :placeholder="$t('taxableIncomeEur')"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('iabPercent')"
          label-for="iabPercent"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <InputNumber
            id="iabPercent"
            v-model="iab.iabPercent"
            mode="decimal"
            :minFractionDigits="2"
            :maxFractionDigits="2"
            suffix=" %"
            locale="de-DE"
            :placeholder="$t('iabPercent')"
          />
        </b-form-group>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import InputNumber from 'primevue/inputnumber';
import Dropdown from 'primevue/dropdown';
import ButtonWait from '@/components/ButtonWait.vue';
import AttachmentList from '@/components/AttachmentList';
import { formatNumber, NUMBER_FORMAT } from '@/helpers/numberFormat';

/** Matches {@link PROJECT_ATTACHMENT_TYPES.FIN_MODEL} on the server */
const FIN_MODEL_ATTACHMENT_TYPE = 'FIN_MODEL';

export default {
  name: 'ProjectInvestmentDeductionAllowance',
  components: {
    InputNumber,
    Dropdown,
    ButtonWait,
    AttachmentList,
  },
  props: {
    project: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      churchTaxRateOptions: [
        { value: 0, label: '0 %' },
        { value: 8, label: '8 %' },
        { value: 9, label: '9 %' },
      ],
    };
  },
  computed: {
    iab() {
      if (!this.project) return null;
      return this.project.investmentDeductionAllowance || null;
    },
    calculatedEEGfeedInTariffEurPerKwhFormatted() {
      return formatNumber(
        this.iab?.calculatedEEGfeedInTariffEurPerKwh,
        NUMBER_FORMAT.DECIMAL_QUINQUIPLE
      );
    },

    finModelAttachments() {
      if (!this.project?.attachments) {
        return [];
      }
      return this.project.attachments.filter(
        (attachment) => attachment.attachmentType === FIN_MODEL_ATTACHMENT_TYPE
      );
    },
  },
  methods: {
    ...mapActions([
      'buildIabExport',
      'fetchIabExportBuildState',
      'buildFinModelExport',
      'fetchAttachmentBuildState',
    ]),
    async startIabExportBuild() {
      if (this.project?.number && this.project.number !== '_new') {
        await this.buildIabExport({ projectNumber: this.project.number });
      }
    },
    async pollIabExportBuildState() {
      if (this.project?.number) {
        await this.fetchIabExportBuildState(this.project.number);
      }
      return this.project?.iabExportAttachmentsBuildState;
    },
    onDeleteIabExportAttachment(event) {
      this.$emit('project-iab-export-attachment-list:delete', {
        attachmentId: event.attachmentId,
        attachmentType: 'iabExportAttachments',
      });
    },
    async startFinModelBuild() {
      if (this.project?.number && this.project.number !== '_new') {
        await this.buildFinModelExport({ projectNumber: this.project.number });
      }
    },
    async pollFinModelBuildState() {
      if (!this.project?.number) {
        return 'NOT_STARTED';
      }
      return await this.fetchAttachmentBuildState({
        projectNumber: this.project.number,
        attachmentType: FIN_MODEL_ATTACHMENT_TYPE,
      });
    },
    onDeleteFinModelAttachment(event) {
      this.$emit('project-iab-export-attachment-list:delete', {
        attachmentId: event.attachmentId,
        attachmentType: FIN_MODEL_ATTACHMENT_TYPE,
      });
    },
  },
  mounted() {
    if (this.project && !this.project.investmentDeductionAllowance) {
      this.$set(this.project, 'investmentDeductionAllowance', {});
    }
  },
};
</script>

<style scoped lang="scss">
.project-investment-deduction-allowance legend {
  margin-top: 1rem;
  margin-bottom: 0.5rem;
}
.project-investment-deduction-allowance legend:first-child {
  margin-top: 0;
}
</style>
